'use strict';

// S200 page configuration for the shared pergola configurator engine.
window.PERGOLA_CONFIG = {
  product: 'S200 Heavy-Duty Motorised Pergola',
  maxSpan: 4300,
  options: {
    install_type: [
      { value: 'Freestanding', svg: 'freestanding' },
      { value: 'Wall-mounted', svg: 'wall' },
      { value: 'Corner wall-mounted', svg: 'corner' },
      { value: 'Not sure', svg: 'unsure' }
    ],
    size_mode: [
      { value: 'standard', label: 'Choose a standard size', note: 'Six published sizes, fastest to quote and install' },
      { value: 'custom', label: 'I need a custom size', note: 'Up to 4.3 m span in the blade direction' }
    ],
    size_standard: [
      { value: '3 × 3 m', note: '9 m² · 1 × 1500N motor' },
      { value: '3 × 4 m', note: '12 m² · 1 × 1500N motor' },
      { value: '3 × 5 m', note: '15 m² · 1 × 1500N motor' },
      { value: '4 × 4 m', note: '16 m² · 1 × 2000N motor' },
      { value: '4 × 5 m', note: '20 m² · 1 × 2000N motor' },
      { value: '4 × 6 m', note: '24 m² · 2 × 2000N motors' }
    ],
    frame_colour: [
      { value: 'Sand-textured grey', frame: 'ls-frame-sand-grey' },
      { value: 'Sand-textured coffee', frame: 'ls-frame-sand-coffee' },
      { value: 'White', frame: 'ls-frame-white' },
      { value: 'Metallic grey', frame: 'ls-frame-metallic' },
      { value: 'Wood-look · premium', label: 'Wood-look', note: 'Premium finish', frame: 'ls-frame-wood' },
      { value: 'Help me choose', label: 'Help me choose', frame: 'ls-frame-help' }
    ],
    blade_light: [
      { value: 'Single-colour LED', label: 'Single-colour LED', note: 'Warm white 3000K · evenly diffused' },
      { value: 'Colour-changing RGB', label: 'Colour-changing RGB', note: 'Flowing · gradient · breathing modes' },
      { value: 'No blade light', note: 'Add lighting any time before production' }
    ],
    trough_light: [
      { value: 'Yes — add trough ambient light', label: 'Add trough light', note: 'Soft glow along the gutter line' },
      { value: 'No trough light', label: 'No thanks' },
      { value: 'Please advise', label: 'Please advise', note: 'We’ll recommend based on your layout' }
    ]
  },
  sync(get, api) {
    const mode = get('size_mode');
    api.show('#standard-size-block', mode === 'standard');
    api.show('#custom-size-block', mode === 'custom');
    if (mode !== 'standard') api.clearRadio('size_standard');
    if (mode !== 'custom') {
      ['size_width', 'size_length', 'size_height'].forEach(name => api.set(name, ''));
    }
    const note = api.note;
    if (mode === 'custom') {
      const width = Number(get('size_width'));
      const length = Number(get('size_length'));
      let text = '';
      if (width > 0 && length > 0) text += `Approximate module area: ${(width * length / 1000000).toFixed(2)} m². `;
      if (width > this.maxSpan) text += 'Outside the published S200 span — your measurements are saved for our team to assess.';
      note('#size-note', text);
    } else {
      note('#size-note', '');
    }
  },
  invalid(step, get, $) {
    const need = (name, message) => !get(name) ? { message, target: document.querySelector(`[name="${name}"]`) } : null;
    if (step === 0) return need('install_type', 'Choose where the pergola will be installed, or select Not sure.') || need('exposure', 'Choose an installation environment, or select Not sure.');
    if (step === 1) {
      const mode = get('size_mode');
      const missing = need('size_mode', 'Choose between a standard size and a custom size.');
      if (missing) return missing;
      if (mode === 'standard') {
        const issue = need('size_standard', 'Choose one of the six standard sizes.');
        if (issue) return issue;
      }
      if (mode === 'custom') {
        for (const [name, label] of [['size_width', 'width'], ['size_length', 'length']]) {
          const number = Number(get(name));
          if (!Number.isFinite(number) || !Number.isInteger(number) || number <= 0)
            return { message: `Enter a positive whole-number ${label} in mm for your module.`, target: $(`#${name}`) };
        }
      }
      const sets = Number(get('identical_sets'));
      if (!Number.isInteger(sets) || sets < 1 || sets > 99) return { message: 'Enter 1 to 99 identical units.', target: $('#identical_sets') };
    }
    if (step === 2) return need('frame_colour', 'Choose a frame finish, or select Help me choose.');
    if (step === 3) return need('blade_light', 'Choose a blade lighting option.') || need('trough_light', 'Choose a trough ambient light option.');
    return null;
  },
  summary(get) {
    const size = get('size_mode') === 'standard'
      ? (get('size_standard') || 'Standard size to be selected')
      : get('size_mode') === 'custom'
        ? `Custom · ${get('size_width') || '—'} × ${get('size_length') || '—'} × ${get('size_height') || '2700'} mm`
        : 'Standard or custom size';
    const rows = [
      ['Installation', [get('install_type'), get('exposure')].filter(Boolean).join(' · ')],
      ['Size', size],
      ['Frame finish', get('frame_colour')],
      ['Blade lighting', get('blade_light')],
      ['Trough light', get('trough_light')]
    ];
    if (get('sensor_interest')) rows.push(['Weather sensor', get('sensor_interest')]);
    if (get('light_control_interest')) rows.push(['Lighting control', get('light_control_interest')]);
    if (get('custom_finish_request')) rows.push(['Special finish', get('custom_finish_request')]);
    rows.push(['Identical units', get('identical_sets') || '—']);
    return rows;
  },
  example(set) {
    const example = {
      install_type: 'Freestanding', exposure: 'Exposed outdoor', size_mode: 'custom',
      size_width: '4000', size_length: '5000', size_height: '2700',
      frame_colour: 'Sand-textured grey', blade_light: 'Colour-changing RGB',
      trough_light: 'Yes — add trough ambient light', sensor_interest: 'Please advise', light_control_interest: 'Yes'
    };
    Object.entries(example).forEach(([name, value]) => set(name, value));
  }
};
